import React, { useState, useEffect } from 'react'; // Force Rebuild 2
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import {
    FiCalendar, FiTrendingUp, FiTrendingDown, FiDollarSign,
    FiPieChart, FiBarChart2, FiX
} from 'react-icons/fi';
import './ReportsPage.css';
import { SyncManager } from '../utils/SyncManager';

const ReportsPage = () => {
    const [reportType, setReportType] = useState('daily');
    // Use business day for default dates
    const getBusinessToday = () => {
        if (SyncManager.getBusinessDate) return SyncManager.getBusinessDate();

        const d = new Date();
        if (d.getHours() < 16) d.setDate(d.getDate() - 1);
        return d.toLocaleDateString('en-CA'); // YYYY-MM-DD
    };

    const [dateRange, setDateRange] = useState({
        startDate: getBusinessToday(),
        endDate: getBusinessToday()
    });

    const { user, isSuperAdmin } = useAuth();
    const { showToast, confirm } = useUI();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(user?.branch_id || '');

    const [dailySales, setDailySales] = useState(null);
    const [topItems, setTopItems] = useState([]);
    const [lowItems, setLowItems] = useState([]);
    const [categorySales, setCategorySales] = useState([]);
    const [sessions, setSessions] = useState([]);

    // Session Modal State
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [sessionModalData, setSessionModalData] = useState(null);

    // Fetch branches if super admin
    useEffect(() => {
        if (isSuperAdmin) {
            const fetchBranches = async () => {
                try {
                    const res = await api.get('/branches');
                    setBranches(res.data);
                    if (res.data.length > 0 && !selectedBranch) {
                        setSelectedBranch(res.data[0].id);
                    }
                } catch (error) {
                    console.error('Failed to fetch branches:', error);
                }
            };
            fetchBranches();
        }
    }, [isSuperAdmin]);

    // Fetch reports when params change
    useEffect(() => {
        if (selectedBranch || !isSuperAdmin) {
            fetchReports();
        }
    }, [reportType, dateRange, selectedBranch]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const branchQuery = isSuperAdmin ? `&branchId=${selectedBranch}` : '';

            if (reportType === 'daily') {
                let ordersData = { orders: [], total_sales: 0, total_orders: 0, total_tax: 0, total_subtotal: 0 };
                try {
                    const response = await api.get(`/reports/daily-sales?date=${dateRange.startDate}${branchQuery}`);
                    ordersData = response.data;
                } catch (err) {
                    console.warn('Network request failed, using offline data only');
                }

                // Merge Pending Offline Orders (if date is today)
                // Use local date for comparison to handle timezone differences
                const getLocalDate = (d) => {
                    const date = d ? new Date(d) : new Date();
                    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
                };
                const todayStr = getLocalDate();



                if (dateRange.startDate === todayStr) {
                    const pendingOrders = await SyncManager.getPendingOrders();
                    if (pendingOrders.length > 0) {
                        // Format pending orders to match report structure
                        const formattedPending = pendingOrders.map(o => ({
                            id: o.offlineId, // Usie offlineId as key
                            order_number: o.orderNumber,
                            offline_id: o.offlineId,
                            created_at: o.createdAt,
                            user_name: 'You (Offline)', // Or fetch current user name
                            // Map camelCase to snake_case but preserve original for calculation safety
                            total_amount: o.totalAmount,
                            subtotal: o.subtotal,
                            tax_amount: o.taxAmount,
                            status: 'draft' // Show as draft or pending-sync
                        }));

                        // Combine (Pending first)
                        // Filter out pending orders that are already in the API response (deduplicate)
                        const uniquePending = formattedPending.filter(p =>
                            !ordersData.orders.some(apiOrder => apiOrder.offline_id === p.offlineId)
                        );

                        ordersData = {
                            ...ordersData,
                            orders: [...uniquePending, ...ordersData.orders],
                            total_orders: parseInt(ordersData.total_orders || 0) + uniquePending.length,
                            total_sales: (parseFloat(ordersData.total_sales) || 0) + uniquePending.reduce((sum, o) => {
                                const val = parseFloat(o.total_amount) || 0;

                                return sum + val;
                            }, 0)
                        };
                    }
                }
                setDailySales(ordersData);
            } else if (reportType === 'monthly') {
                const response = await api.get(`/reports/daily-sales?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}${branchQuery}`);
                setDailySales(response.data);
            } else if (reportType === 'items') {
                const [topRes, lowRes] = await Promise.all([
                    api.get(`/reports/item-performance?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=10&sort=desc${branchQuery}`),
                    api.get(`/reports/item-performance?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=10&sort=asc${branchQuery}`)
                ]);
                setTopItems(topRes.data);
                setLowItems(lowRes.data);
            } else if (reportType === 'category') {
                const response = await api.get(`/reports/category-sales?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}${branchQuery}`);
                setCategorySales(response.data);
            } else if (reportType === 'sessions') {
                let sessionData = [];
                try {
                    const response = await api.get(`/reports/sessions?${branchQuery ? branchQuery.substring(1) : ''}`);
                    sessionData = response.data;
                } catch (err) {
                    console.warn('Sessions API failed, checking offline cache');
                }

                // Merge Offline Status & Stats
                const localSession = await SyncManager.getCachedSession();
                const pendingOrders = await SyncManager.getPendingOrders();

                if (localSession) {

                }

                if (pendingOrders.length > 0) {

                }

                if (localSession || pendingOrders.length > 0) {
                    const todayStr = new Date().toISOString().slice(0, 10);

                    // Normalize keys
                    const isLocalOpen = localSession ? (localSession.isOpen !== undefined ? localSession.isOpen : localSession.is_open) : true;
                    const localBusinessDate = localSession?.businessDate || localSession?.business_date || todayStr;
                    const localOpenedAt = localSession?.openedAt || localSession?.opened_at;
                    const localClosedAt = localSession?.closedAt || localSession?.closed_at;

                    // Use local date for comparison to handle timezone differences
                    const getLocalDate = (d) => {
                        const date = d ? new Date(d) : new Date();
                        return date.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
                    };

                    const localDate = getLocalDate(localBusinessDate);

                    // Calculate pending stats
                    const pendingSales = pendingOrders.reduce((sum, o) => {
                        // Robust property access
                        const amount = parseFloat(o.totalAmount || o.total_amount || 0);
                        // console.log('DEBUG: Order amount:', amount, o);
                        return sum + amount;
                    }, 0);
                    const pendingCount = pendingOrders.length;



                    const topIndex = sessionData.findIndex(s => {
                        const sDate = s.business_date && getLocalDate(s.business_date);
                        const match = sDate === localDate;
                        // console.log('DEBUG: Checking session:', sDate, localDate, match); 
                        return match;
                    });



                    if (topIndex >= 0) {
                        // Merge stats into existing session
                        if (sessionData[topIndex].is_open || isLocalOpen) {
                            sessionData[topIndex] = {
                                ...sessionData[topIndex],
                                total_sales: parseFloat(sessionData[topIndex].total_sales || 0) + pendingSales,
                                order_count: parseInt(sessionData[topIndex].order_count || 0) + pendingCount
                            };
                        }

                        // Compare Timestamps to determine Truth for Status
                        const getTime = (d) => d ? new Date(d).getTime() : 0;
                        const apiSess = sessionData[topIndex];
                        const apiTime = Math.max(getTime(apiSess.opened_at), getTime(apiSess.closed_at));
                        const localTime = Math.max(getTime(localOpenedAt), getTime(localClosedAt));

                        if (localTime > apiTime && localSession) {
                            sessionData[topIndex] = {
                                ...sessionData[topIndex],
                                is_open: isLocalOpen ? 1 : 0,
                                closed_at: isLocalOpen ? null : (localClosedAt || new Date().toISOString()),
                                opened_at: isLocalOpen ? (localOpenedAt || new Date().toISOString()) : sessionData[topIndex].opened_at
                            };
                        }
                    } else if (isLocalOpen || pendingCount > 0) {
                        sessionData.unshift({
                            id: 'offline_pending',
                            business_date: localBusinessDate,
                            opened_at: localOpenedAt || new Date().toISOString(),
                            closed_at: null,
                            is_open: 1,
                            total_sales: pendingSales,
                            order_count: pendingCount
                        });
                    }
                }
                setSessions(sessionData);
            }
        } catch (error) {
            console.error('Failed to fetch reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSessionClick = async (session) => {
        try {
            setLoading(true);

            // Mock session orders for offline display or if API fails
            // Use local date for comparison to handle timezone differences
            const getLocalDate = (d) => {
                const date = d ? new Date(d) : new Date();
                return date.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
            };

            const todayStr = getLocalDate();
            const sessionDate = getLocalDate(session.business_date);
            // Allow merging if it's today's session OR if the session is currently open (regardless of start date) OR it's a temp offline session
            const isToday = sessionDate === todayStr || session.is_open || session.id === 'offline_pending';



            let sessionOrders = [];
            let sessionInfo = { ...session };

            // 1. Try to fetch from API (Service Worker should handle cache if offline)
            let apiResponseLength = 0;
            if (session.id !== 'offline_pending') {
                try {
                    const branchQuery = isSuperAdmin ? `&branchId=${selectedBranch}` : '';
                    const response = await api.get(`/reports/daily-sales?sessionId=${session.id}${branchQuery}`);
                    sessionOrders = response.data.orders || [];
                    sessionInfo = { ...sessionInfo, ...response.data };
                    apiResponseLength = sessionOrders.length;
                } catch (err) {
                    console.warn('Failed to fetch session details from API, falling back to local data:', err);
                    if (!isToday) throw err; // Only fallback for today's session
                }
            }

            // 2. Merge Pending Offline Orders (if session is for Today)
            if (isToday) {
                const pendingOrders = await SyncManager.getPendingOrders();
                if (pendingOrders.length > 0) {
                    // Format pending orders
                    const formattedPending = pendingOrders.map(o => ({
                        id: o.offlineId,
                        order_number: o.orderNumber,
                        offline_id: o.offlineId,
                        created_at: o.createdAt,
                        user_name: 'You (Offline)',
                        total_amount: o.totalAmount,
                        status: 'draft'
                    }));

                    // Deduplicate
                    // Filter out pending orders that are already in the API response
                    const uniquePending = formattedPending.filter(p =>
                        !sessionOrders.some(apiOrder => apiOrder.offline_id === p.offlineId)
                    );

                    sessionOrders = [...uniquePending, ...sessionOrders];

                    // Update totals if falling back completely or just merging
                    if (session.id === 'offline_pending' || !navigator.onLine || sessionOrders.length !== apiResponseLength) {
                        const pendingTotal = uniquePending.reduce((sum, o) => {
                            const val = parseFloat(o.total_amount) || 0;
                            return sum + val;
                        }, 0);

                        sessionInfo.total_sales = (parseFloat(sessionInfo.total_sales) || 0) + pendingTotal;
                        sessionInfo.total_orders = (parseInt(sessionInfo.total_orders) || 0) + uniquePending.length;
                    }
                }
            }

            setSessionModalData({
                orders: sessionOrders,
                total_sales: sessionInfo.total_sales,
                total_orders: sessionInfo.total_orders,
                sessionInfo: session
            });
            setShowSessionModal(true);
        } catch (error) {
            console.error('Failed to fetch session details:', error);
            showToast('Failed to load session details (Offline)', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return 'Rs. ' + (parseFloat(value) || 0).toFixed(2);
    };

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);

    const handleOrderClick = async (orderId) => {
        try {
            // Check if it's an offline order (starts with OFF-)
            if (typeof orderId === 'string' && orderId.startsWith('OFF-')) {
                const pendingOrders = await SyncManager.getPendingOrders();
                const offlineOrder = pendingOrders.find(o => o.offlineId === orderId);
                if (offlineOrder) {
                    setSelectedOrder({
                        ...offlineOrder,
                        offline_id: offlineOrder.offlineId,
                        created_at: offlineOrder.createdAt,
                        total_amount: offlineOrder.totalAmount,
                        subtotal: offlineOrder.subtotal,
                        tax_amount: offlineOrder.taxAmount,
                        tax_rate: offlineOrder.taxRate,
                        items: offlineOrder.items.map(i => ({
                            item_name: i.itemName || i.name,
                            quantity: i.quantity,
                            unit_price: i.unitPrice || i.price,
                            total_price: i.totalPrice || (i.price * i.quantity)
                        })),
                        first_name: 'You',
                        last_name: '(Offline)',
                    });
                    setShowOrderModal(true);
                    return;
                }
            }

            // check if order exists in the current reports list (dailySales)
            if (dailySales && dailySales.orders) {
                const localOrder = dailySales.orders.find(o => o.id === orderId);
                // IF we have the order locally AND it has items (crucial for details), use it
                if (localOrder && localOrder.items && localOrder.items.length > 0) {
                    setSelectedOrder(localOrder);
                    setShowOrderModal(true);
                    return;
                }
            }

            // Fallback to fetching from API (will fail if offline and not cached)
            const res = await api.get(`/orders/${orderId}`);
            setSelectedOrder(res.data);
            setShowOrderModal(true);
        } catch (error) {
            console.error('Failed to fetch order details:', error);
            // If we are offline and failed to fetch, try to one last effort to find it in the list even if items might be missing,
            // or just show a more specific error
            if (!navigator.onLine) {
                if (dailySales && dailySales.orders) {
                    const localOrder = dailySales.orders.find(o => o.id === orderId);
                    if (localOrder) {
                        // Show what we have, even if items are missing (better than error)
                        setSelectedOrder({
                            ...localOrder,
                            items: localOrder.items || [] // Ensure items array exists
                        });
                        setShowOrderModal(true);
                        if (!localOrder.items || localOrder.items.length === 0) {
                            showToast('Order items details not available offline.', 'warning');
                        }
                        return;
                    }
                }
                showToast('Order details not available offline.', 'error');
            } else {
                showToast('Failed to fetch order details.', 'error');
            }
        }
    };

    return (
        <div className="reports-page">
            <div className="page-header">
                <div>
                    <h1>Reports</h1>
                    <p className="text-muted">Analyze your sales and performance</p>
                </div>
                {isSuperAdmin && (
                    <div className="branch-selector">
                        <select
                            className="form-select"
                            value={selectedBranch}
                            onChange={(e) => setSelectedBranch(e.target.value)}
                        >
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Report Type Tabs */}
            <div className="report-tabs">
                <button
                    className={`report-tab ${reportType === 'daily' ? 'active' : ''}`}
                    onClick={() => setReportType('daily')}
                >
                    <FiDollarSign /> Daily Sales
                </button>
                <button
                    className={`report-tab ${reportType === 'items' ? 'active' : ''}`}
                    onClick={() => setReportType('items')}
                >
                    <FiBarChart2 /> Item Performance
                </button>
                <button
                    className={`report-tab ${reportType === 'category' ? 'active' : ''}`}
                    onClick={() => setReportType('category')}
                >
                    <FiPieChart /> Category Sales
                </button>
                <button
                    className={`report-tab ${reportType === 'sessions' ? 'active' : ''}`}
                    onClick={() => setReportType('sessions')}
                >
                    <FiCalendar /> Business Days
                </button>
                <button
                    className={`report-tab ${reportType === 'monthly' ? 'active' : ''}`}
                    onClick={() => setReportType('monthly')}
                >
                    <FiCalendar /> Monthly Sales
                </button>
            </div>

            {/* Date Filters */}
            <div className="date-filters" style={{ alignItems: 'flex-start' }}>
                <div className="form-group">
                    <label className="form-label">
                        {reportType === 'daily' ? 'Current Session' : 'Start Date'}
                    </label>
                    {reportType === 'daily' ? (
                        <div className="session-navigator" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="session-display" style={{
                                background: '#e3f2fd',
                                color: '#1976d2',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: '500',
                                fontSize: '0.95rem',
                                border: '1px solid #bbdefb',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span style={{ marginRight: '5px' }}>📅</span>
                                {(() => {
                                    const date = new Date(dateRange.startDate);
                                    const nextDay = new Date(date);
                                    nextDay.setDate(date.getDate() + 1);

                                    const formatDate = (d) => {
                                        const day = d.getDate();
                                        const month = d.toLocaleString('default', { month: 'short' });
                                        const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3) ? 0 : (day % 100 - day % 10 !== 10) * day % 10];
                                        return `${day}${suffix} ${month}`;
                                    };

                                    return `${formatDate(date)} - ${formatDate(nextDay)}`;
                                })()}
                            </div>
                        </div>
                    ) : (
                        <input
                            type="date"
                            className="form-input"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                        />
                    )}
                </div>
                {(reportType === 'monthly' || (reportType !== 'daily' && reportType !== 'sessions')) && (
                    <div className="form-group">
                        <label className="form-label">End Date</label>
                        <input
                            type="date"
                            className="form-input"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                        />
                    </div>
                )}
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="spinner spinner-lg"></div>
                </div>
            ) : (
                <>
                    {/* Daily/Monthly Sales Report */}
                    {(reportType === 'daily' || reportType === 'monthly') && dailySales && (
                        <div className="report-content">
                            <div className="stats-grid">
                                <div className="stat-card large">
                                    <div className="stat-icon sales">
                                        <FiDollarSign />
                                    </div>
                                    <div className="stat-content">
                                        <h3>{formatCurrency(dailySales.total_sales)}</h3>
                                        <p>Total Sales</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{dailySales.total_orders || 0}</h3>
                                        <p>Total Orders</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{formatCurrency(dailySales.total_subtotal)}</h3>
                                        <p>Subtotal</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{formatCurrency(dailySales.total_tax)}</h3>
                                        <p>Tax Collected</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{dailySales.cancelled_orders || 0}</h3>
                                        <p>Cancelled</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{dailySales.returned_orders || 0}</h3>
                                        <p>Returned</p>
                                    </div>
                                </div>
                            </div>

                            {/* Daily Orders List */}
                            <div className="card mt-4">
                                <div className="card-header">
                                    <h3>Order History ({reportType === 'daily'
                                        ? new Date(dateRange.startDate).toLocaleDateString()
                                        : `${new Date(dateRange.startDate).toLocaleDateString()} - ${new Date(dateRange.endDate).toLocaleDateString()}`
                                    })</h3>
                                </div>
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Order #</th>
                                                <th>Offline ID</th>
                                                <th>Date</th>
                                                <th>Time</th>
                                                <th>Staff</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dailySales.orders && dailySales.orders.length > 0 ? (
                                                dailySales.orders.map(order => (
                                                    <tr key={order.id}>
                                                        <td>
                                                            <button
                                                                className="btn-link"
                                                                onClick={() => handleOrderClick(order.id)}
                                                                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                                            >
                                                                {order.order_number}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            {order.offline_id ? (
                                                                <span className="text-muted text-sm">{order.offline_id}</span>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                                        <td>{new Date(order.created_at).toLocaleTimeString()}</td>
                                                        <td>{order.user_name || 'Unknown'}</td>
                                                        <td className="font-medium">{formatCurrency(order.total_amount)}</td>
                                                        <td>
                                                            <span className={`badge badge-${order.status === 'completed' ? 'success' :
                                                                order.status === 'cancelled' ? 'error' :
                                                                    order.status === 'returned' ? 'warning' : 'warning'
                                                                }`}>
                                                                {order.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted">No orders for this period</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Item Performance Report */}
                    {reportType === 'items' && (
                        <div className="report-content">
                            <div className="performance-grid">
                                <div className="card">
                                    <div className="card-header">
                                        <h3><FiTrendingUp className="text-success" /> Top Selling Items</h3>
                                    </div>
                                    <div className="card-body">
                                        {topItems.length === 0 ? (
                                            <p className="text-muted text-center">No data available</p>
                                        ) : (
                                            <div className="items-list">
                                                {topItems.map((item, idx) => (
                                                    <div key={idx} className="item-row">
                                                        <span className="item-rank">{idx + 1}</span>
                                                        <div className="item-info">
                                                            <div className="item-name">{item.item_name}</div>
                                                            <div className="item-category">{item.category_name}</div>
                                                        </div>
                                                        <div className="item-stats">
                                                            <div className="item-qty">{item.total_quantity} sold</div>
                                                            <div className="item-revenue">{formatCurrency(item.total_revenue)}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-header">
                                        <h3><FiTrendingDown className="text-error" /> Low Selling Items</h3>
                                    </div>
                                    <div className="card-body">
                                        {lowItems.length === 0 ? (
                                            <p className="text-muted text-center">No data available</p>
                                        ) : (
                                            <div className="items-list">
                                                {lowItems.map((item, idx) => (
                                                    <div key={idx} className="item-row">
                                                        <span className="item-rank low">{idx + 1}</span>
                                                        <div className="item-info">
                                                            <div className="item-name">{item.item_name}</div>
                                                            <div className="item-category">{item.category_name}</div>
                                                        </div>
                                                        <div className="item-stats">
                                                            <div className="item-qty">{item.total_quantity} sold</div>
                                                            <div className="item-revenue">{formatCurrency(item.total_revenue)}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Category Sales Report */}
                    {reportType === 'category' && (
                        <div className="report-content">
                            <div className="card">
                                <div className="card-header">
                                    <h3>Sales by Category</h3>
                                </div>
                                <div className="card-body">
                                    {categorySales.length === 0 ? (
                                        <p className="text-muted text-center">No data available</p>
                                    ) : (
                                        <div className="category-bars">
                                            {categorySales.map((cat, idx) => {
                                                const maxRevenue = Math.max(...categorySales.map(c => parseFloat(c.total_revenue)));
                                                const percentage = (parseFloat(cat.total_revenue) / maxRevenue) * 100;
                                                return (
                                                    <div key={idx} className="category-bar-row">
                                                        <div className="category-label">
                                                            <span className="category-name">{cat.category_name}</span>
                                                            <span className="category-revenue">{formatCurrency(cat.total_revenue)}</span>
                                                        </div>
                                                        <div className="category-bar-container">
                                                            <div
                                                                className="category-bar"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="category-orders">{cat.order_count} orders</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Business Days Report */}
                    {reportType === 'sessions' && (
                        <div className="report-content">
                            <div className="card">
                                <div className="card-header">
                                    <h3>Business Day Sessions</h3>
                                </div>
                                <div className="table-container">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Business Date</th>
                                                <th>Opened At</th>
                                                <th>Closed At</th>
                                                <th>Orders</th>
                                                <th>Total Sales</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sessions.map((session) => (
                                                <tr key={session.id}>
                                                    <td>
                                                        <button
                                                            className="btn-link"
                                                            onClick={() => handleSessionClick(session)}
                                                            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                                        >
                                                            {new Date(session.business_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                        </button>
                                                    </td>
                                                    <td>{new Date(session.opened_at).toLocaleTimeString()}</td>
                                                    <td>{session.closed_at ? new Date(session.closed_at).toLocaleTimeString() : '-'}</td>
                                                    <td>{session.order_count || 0}</td>
                                                    <td>{formatCurrency(session.total_sales)}</td>
                                                    <td>
                                                        <span className={`badge badge-${session.is_open ? 'success' : 'gray'}`}>
                                                            {session.is_open ? 'Open' : 'Closed'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {sessions.length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted">
                                                        No sessions found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Session Details Modal */}
            {showSessionModal && sessionModalData && (
                <div className="modal-overlay" onClick={() => setShowSessionModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <div>
                                <h3 className="modal-title">Session: {new Date(sessionModalData.sessionInfo?.business_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}</h3>
                                <p className="text-muted" style={{ margin: 0, fontSize: '0.9rem' }}>
                                    {new Date(sessionModalData.sessionInfo?.opened_at).toLocaleTimeString()} -
                                    {sessionModalData.sessionInfo?.closed_at ? new Date(sessionModalData.sessionInfo.closed_at).toLocaleTimeString() : 'Open'}
                                </p>
                            </div>
                            <button className="modal-close" onClick={() => setShowSessionModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="stats-grid mb-4" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{sessionModalData.total_sales ? formatCurrency(sessionModalData.total_sales) : 'Rs. 0.00'}</h3>
                                        <p>Sales</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{sessionModalData.total_orders || 0}</h3>
                                        <p>Orders</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{sessionModalData.cancelled_orders || 0}</h3>
                                        <p>Cancelled</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-content">
                                        <h3>{sessionModalData.returned_orders || 0}</h3>
                                        <p>Returned</p>
                                    </div>
                                </div>
                            </div>

                            <div className="table-container">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Order #</th>
                                            <th>Offline ID</th>
                                            <th>Time</th>
                                            <th>Staff</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessionModalData.orders && sessionModalData.orders.length > 0 ? (
                                            sessionModalData.orders.map(order => (
                                                <tr key={order.id}>
                                                    <td>{order.order_number}</td>
                                                    <td>
                                                        {order.offline_id ? (
                                                            <span className="text-muted text-sm">{order.offline_id}</span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>{new Date(order.created_at).toLocaleTimeString()}</td>
                                                    <td>{order.user_name || 'Unknown'}</td>
                                                    <td className="font-medium">{formatCurrency(order.total_amount)}</td>
                                                    <td>
                                                        <span className={`badge badge-${order.status === 'completed' ? 'success' :
                                                            order.status === 'cancelled' ? 'error' :
                                                                order.status === 'returned' ? 'warning' : 'warning'
                                                            }`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted">No orders for this session</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowSessionModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Order Details Modal */}
            {showOrderModal && selectedOrder && (
                <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h3 className="modal-title">Order Details: {selectedOrder.orderNumber}</h3>
                            <button className="modal-close" onClick={() => setShowOrderModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="order-info mb-4">
                                <div className="flex justify-between mb-2">
                                    <span className="text-muted">Date:</span>
                                    <span>{new Date(selectedOrder.created_at).toLocaleString()}</span>
                                </div>
                                {(selectedOrder.offline_id || selectedOrder.offlineId) && (
                                    <div className="flex justify-between mb-2">
                                        <span className="text-muted">Offline ID:</span>
                                        <span>{selectedOrder.offline_id || selectedOrder.offlineId}</span>
                                    </div>
                                )}
                                <div className="flex justify-between mb-2">
                                    <span className="text-muted">Staff:</span>
                                    <span>{selectedOrder.first_name} {selectedOrder.last_name}</span>
                                </div>
                                <div className="flex justify-between mb-2">
                                    <span className="text-muted">Status:</span>
                                    <span className={`badge badge-${selectedOrder.status === 'completed' ? 'success' : 'warning'}`}>
                                        {selectedOrder.status}
                                    </span>
                                </div>
                            </div>

                            <div className="table-container mb-4">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Qty</th>
                                            <th>Price</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.items && selectedOrder.items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{item.item_name}</td>
                                                <td>{item.quantity}</td>
                                                <td>{formatCurrency(item.unit_price)}</td>
                                                <td>{formatCurrency(item.total_price)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="order-totals" style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '1rem' }}>
                                <div className="flex justify-between mb-2">
                                    <span>Subtotal</span>
                                    <span>{formatCurrency(selectedOrder.subtotal)}</span>
                                </div>
                                <div className="flex justify-between mb-2">
                                    <span>Tax ({selectedOrder.tax_rate}%)</span>
                                    <span>{formatCurrency(selectedOrder.tax_amount)}</span>
                                </div>
                                <div className="flex justify-between font-bold text-lg mt-2">
                                    <span>Total</span>
                                    <span className="text-primary">{formatCurrency(selectedOrder.total_amount)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {selectedOrder.status === 'completed' && (
                                <button
                                    className="btn btn-danger"
                                    onClick={async () => {
                                        const confirmed = await confirm('Are you sure you want to return this order? Inventory will be restored.', 'Confirm Return');
                                        if (confirmed) {
                                            try {
                                                await api.post(`/orders/${selectedOrder.id}/return`);
                                                setShowOrderModal(false);
                                                showToast('Order returned successfully', 'success');
                                                fetchReports(); // Refresh data
                                            } catch (error) {
                                                console.error('Failed to return order:', error);
                                                showToast('Failed to return order', 'error');
                                            }
                                        }
                                    }}
                                >
                                    Return Order
                                </button>
                            )}
                            <button className="btn btn-secondary" onClick={() => setShowOrderModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsPage;
